import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, ShieldAlert, GanttChartSquare, Stamp, Gauge, ShieldCheck, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SELLING_POINTS = [
  { icon: ShieldAlert, title: "RAID that stands up to audit", body: "Risks, issues, assumptions, decisions, change requests and lessons — with owners, scoring and history." },
  { icon: GanttChartSquare, title: "Plans with real baselines", body: "Gantt scheduling, drag-and-drop and re-baselining, so slippage shows against what you committed to." },
  { icon: Stamp, title: "Stage gates that gate", body: "Named approvers and routed sign-off on gates, changes, decisions and reports." },
  { icon: Gauge, title: "Compliance you can score", body: "Every project measured against your own governance framework, rolled up to portfolio level." },
  { icon: ShieldCheck, title: "AI inside your own tenant", body: "Point it at your Azure OpenAI resource — or switch AI off entirely and lose nothing else." },
];


// The demo login hint is a development convenience. Never show it on a customer-facing domain.
const SHOW_DEMO_HINT = /(^localhost$|^127\.0\.0\.1$|\.preview\.emergentagent\.com$)/.test(
  window.location.hostname
);

export default function Login() {
  const { login } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="marketing-root grid min-h-screen grid-cols-1 lg:grid-cols-2">
      {/* Selling panel */}
      <div
        data-testid="login-selling-panel"
        className="grain-overlay relative hidden flex-col justify-between overflow-hidden p-12 text-white lg:flex lg:p-16"
        style={{ backgroundColor: branding.primary_color || "#1F4E79" }}
      >
        <div className="relative z-10 flex items-center justify-between">
          <Link to="/" className="font-marketing-display text-lg font-black tracking-tight text-white">
            {branding.product_name}
          </Link>
          <Link
            to="/pricing"
            data-testid="login-pricing-link"
            className="text-xs font-semibold uppercase tracking-[0.15em] text-white/60 transition-colors hover:text-white"
          >
            Pricing
          </Link>
        </div>

        <div className="relative z-10 max-w-xl">
          <h1 className="font-marketing-display text-4xl font-black leading-none tracking-tight text-white lg:text-5xl">
            Portfolio governance,
            <br />
            without the platform lock-in.
          </h1>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-white/70">
            The white-label PPM platform for PMOs that have to evidence delivery — not just track tasks.
          </p>

          <ul className="mt-10 space-y-5">
            {SELLING_POINTS.map((p) => (
              <li key={p.title} className="flex gap-4" data-testid={`login-benefit-${p.title.split(" ")[0].toLowerCase()}`}>
                <p.icon size={18} strokeWidth={1.5} className="mt-0.5 shrink-0 text-white/70" />
                <div>
                  <p className="text-sm font-semibold text-white">{p.title}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-white/60">{p.body}</p>
                </div>
              </li>
            ))}
          </ul>

          <Link
            to="/signup"
            data-testid="login-trial-cta"
            className="mt-10 inline-flex items-center gap-2 border border-white/30 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-slate-900"
          >
            Start 7-day free trial <ArrowRight size={15} strokeWidth={1.5} />
          </Link>
          <p className="mt-3 flex items-center gap-1.5 text-xs text-white/50">
            <Check size={12} strokeWidth={2} /> No card required. Nothing auto-renews.
          </p>
        </div>

        <div className="relative z-10 text-[11px] uppercase tracking-[0.2em] text-white/35">{branding.org_name}</div>
      </div>

      {/* Sign-in */}
      <div className="flex flex-1 items-center justify-center bg-white px-6 py-16">
        <form onSubmit={submit} data-testid="login-form" className="w-full max-w-sm stagger-in">
          <Link to="/" className="font-marketing-display text-base font-black tracking-tight lg:hidden">
            {branding.product_name}
          </Link>
          <h2 className="mt-6 font-marketing-display text-3xl font-black tracking-tight text-slate-900 lg:mt-0">Sign in</h2>
          <p className="mt-2 text-sm text-slate-500">Access your delivery workspace.</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="field-label">Email</label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="field-label">Password</label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          <Button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="mt-6 w-full gap-2"
            style={{ backgroundColor: branding.primary_color }}
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : <ArrowRight size={16} />}
            Sign in
          </Button>

          <p className="mt-6 text-center text-sm text-slate-500">
            New to {branding.product_name}?{" "}
            <Link to="/signup" data-testid="login-signup-link" className="font-semibold text-[color:var(--marketing-primary)] transition-colors hover:underline">
              Start a free trial
            </Link>
          </p>

          {SHOW_DEMO_HINT && (
            <div data-testid="login-demo-hint" className="mt-8 rounded border border-slate-200 bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-500">
              <span className="font-semibold text-slate-700">Demo admin</span>
              <br />
              admin@harmony.io / Harmony123!
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
